import { sortBy, isEmpty } from 'lodash'
import moment from 'moment'

import { capitalize } from 'utils/strings'
import { hasKey } from 'utils/obj'
import SvgStack from 'components/icons/Stack'
import SvgCube from 'components/icons/Cube'
import SvgHelm from 'components/icons/Helm'
import SvgHostname from 'components/icons/attacks/Hostname'
import SquareGlyph from 'components/common/SquareGlyph'
import { HeptagonGlyphIcon } from 'components/common/GlyphIcon'

// We needed to know what client versions are considered "new" for the onboarding dashboard
// This version number comes from Phil. 2.13.0 or higher is new
// The team is looking into a way to add this information to an endpoint so we don't have to hardcode it like this
export const NEW_CLIENT_VERSION_MIN = [2, 13, 0]
export const NEW_WINDOWS_CLIENT_VERSION_MIN = [1, 0, 6]

export const TARGET_TYPES = {
  Host: {
    type: 'Host',
    label: 'Hosts',
    path: 'hosts',
    item: 'host',
    SvgIcon: SvgStack,
    SvgGlyphIcon: SquareGlyph
  },
  Container: {
    type: 'Container',
    label: 'Containers',
    path: 'containers',
    item: 'container',
    SvgIcon: SvgCube,
    SvgGlyphIcon: SquareGlyph
  },
  Kubernetes: {
    type: 'Kubernetes',
    label: 'Kubernetes',
    path: 'kubernetes',
    item: 'pod',
    SvgIcon: SvgHelm,
    SvgGlyphIcon: HeptagonGlyphIcon
  },
  Applications: {
    type: 'Applications',
    label: 'Applications',
    path: 'applications',
    item: 'application',
    SvgIcon: SvgHostname,
    SvgGlyphIcon: SquareGlyph
  }
}

export const isHost = targetType => targetType === TARGET_TYPES.Host.type
export const isContainer = targetType =>
  targetType === TARGET_TYPES.Container.type
export const isKubernetes = targetType =>
  targetType === TARGET_TYPES.Kubernetes.type

export const isActive = target => target.state === 'ACTIVE'

export const getTargetType = targetType => {
  if (targetType === 'Hosts') {
    return TARGET_TYPES.Host.type
  }

  if (targetType === 'Containers') {
    return TARGET_TYPES.Container.type
  }
  return targetType
}

export const isHostTarget = target => hasKey(target, 'identifier')

export const getUniqueTargetId = target =>
  isHostTarget(target) ? target.identifier : target.id

export const getTargetDisplayName = target =>
  isHostTarget(target) ? target.identifier : target.name

export const getTargetParams = target =>
  isHostTarget(target) ? target.tags : target.labels

export const isTargetParamsEmpty = targetParams =>
  isEmpty(targetParams) ||
  Object.values(targetParams).every(paramValues => {
    if (paramValues instanceof Set) {
      return paramValues.size === 0
    } else {
      return !paramValues
    }
  })

export const getContainersFromClients = clients =>
  clients.flatMap(c => c.containers)

export const getAllTargetsIds = ({ clients = [], targetType }) => {
  switch (targetType) {
    case 'Containers': {
      return clients.reduce((accum, { containers = [] }) => {
        Array.prototype.push.apply(
          accum,
          containers.map(({ id }) => id)
        )
        return accum
      }, [])
    }
    case 'Hosts': {
      return clients.map(client => client.identifier)
    }
  }
}

export const getCurrentTargetsIds = (clients, targets = [], targetType) => {
  if (targets.length === 0) return targets

  const allTargetsIds = getAllTargetsIds({ clients, targetType })
  return targets.filter(target => allTargetsIds.includes(target))
}

// Shape of labelKeys
/* {
    <key>: {
      clients: {
        <client1>: true/false
      },
      values: {
        <value1>: true/false
      }
    }
  } */

// Shape of labelKeys
/* {
    ['<key>:<value>', '<key2>:<value2>']
    }
  } */

// Shape of selectedSets
/* {
      <key>: <value>
    }
  } */
export const formatTagsAndLabels = (
  targets,
  targetType,
  selectedTargetParams = {}
) => {
  let idKey
  let paramsKey
  let availableTargets

  switch (targetType) {
    case 'Containers':
      idKey = 'id'
      paramsKey = 'labels'
      availableTargets = getContainersFromClients(targets)
      break
    case 'Hosts':
    default:
      idKey = 'identifier'
      paramsKey = 'tags'
      availableTargets = targets
      break
  }

  // Grab targets that match current selection
  const selectedTargets = availableTargets.filter(target =>
    isTargetSelected(
      target,
      'Random',
      targetType,
      [],
      selectedTargetParams,
      false
    )
  )

  // Create singular set of alike tags/labels
  const combined = selectedTargets.reduce((combinedParams, target) => {
    const params = target[paramsKey]
    Object.entries(params).forEach(([key, value]) => {
      if (!combinedParams[key]) {
        combinedParams[key] = new Set()
      }

      combinedParams[key].add(value)
    })

    return combinedParams
  }, {})

  const out = availableTargets.reduce(
    ({ keyValueSets, labelKeys }, target) => {
      const id = target[idKey]
      const params = target[paramsKey]

      Object.entries(params).forEach(([key, value]) => {
        if (!labelKeys[key]) {
          labelKeys[key] = {
            name: key,
            targets: [],
            values: new Set(),
            disabled: new Set()
          }
        }

        // Add key-value to the set
        keyValueSets.add(`${key}:"${value}"`)
        labelKeys[key].values.add(value)
        labelKeys[key].targets.push(id)

        // If no target params have been selected yet; skip adding anything to the disabled list
        if (isTargetParamsEmpty(selectedTargetParams)) {
          return
        }

        if (combined[key] && combined[key].has(value)) {
          labelKeys[key].disabled.delete(value)
        } else {
          labelKeys[key].disabled.add(value)
        }
      })
      return { keyValueSets, labelKeys }
    },
    {
      keyValueSets: new Set(),
      labelKeys: {}
    }
  )
  //  console.log(out)
  return { availableTargetParams: out.keyValueSets, labelKeys: out.labelKeys }
}

export const isTargetSelected = (
  target,
  targetingStrategy,
  targetType,
  selectedTargets = [],
  selectedTargetParams = {},
  targetAll
) => {
  if (
    selectedTargets.length === 0 &&
    targetingStrategy.toLowerCase() !== 'random'
  ) {
    return false
  }

  if (selectedTargets.length) {
    return selectedTargets.some(
      targetId => targetId === target.identifier || targetId === target.id
    )
  }

  let params = targetType === 'Hosts' ? target.tags : target.labels

  // If there are no target params with selected values, all targets are considered selected
  // But we have switched our UI to show none selected by default to "start safe"
  // This is overriden later on with the "Target all hosts" option
  // This creates a default state of no targets being selected
  if (targetAll) {
    if (isTargetParamsEmpty(selectedTargetParams)) return true
  } else {
    if (isTargetParamsEmpty(selectedTargetParams)) return false
  }
  //  console.log('selectedTargetParams', selectedTargetParams)
  return Object.entries(selectedTargetParams).every(
    ([key, values = new Set()]) => {
      if (values instanceof Set) {
        if (values.size === 0) return true // skip empty sets
        return values.has(params[key])
      } else {
        return params && params[key] === values
      }
    }
  )
}

/**
 * Returns the clients that would be targeted for either host or container based attacks
 *
 * @param clients
 * @param targetingStrategy
 * @param targetType
 * @param selectedTargets
 * @param params
 * @param targetAll
 * @returns {[]} selected clients
 */
export const getSelectedClients = (
  clients,
  targetingStrategy,
  targetType,
  selectedTargets,
  params,
  targetAll
) => {
  return clients.reduce((accum, target) => {
    if (targetType === 'Hosts') {
      const isClientSelected = isTargetSelected(
        target,
        targetingStrategy,
        targetType,
        selectedTargets,
        params,
        targetAll
      )
      if (isClientSelected) {
        accum.push(target)
      }
    } else if (targetType === 'Containers') {
      const { containers } = target
      for (let i = 0; i < containers.length; ++i) {
        const container = containers[i]
        const isContainerSelected = isTargetSelected(
          container,
          targetingStrategy,
          targetType,
          selectedTargets,
          params,
          targetAll
        )

        if (isContainerSelected) {
          accum.push(target)
          break
        }
      }
    }
    return accum
  }, [])
}

/**
 * Returns the relevent selected targets for host or container attacks
 * if the targetType is Hosts then this will return clients
 * else if targetType is Containers it will return the targeted containers
 *
 * @param clients
 * @param targetingStrategy
 * @param targetType
 * @param selectedTargets
 * @param params
 * @param targetAll
 * @returns {*}
 */
export const getSelectedTargets = (
  clients,
  targetingStrategy,
  targetType,
  selectedTargets,
  params,
  targetAll
) => {
  let targets
  if (targetType === 'Hosts') {
    targets = clients
  } else {
    targets = getContainersFromClients(clients)
  }

  return targets.filter(target =>
    isTargetSelected(
      target,
      targetingStrategy,
      targetType,
      selectedTargets,
      params,
      targetAll
    )
  )
}

// Get total count of client available by targetParams selected
export const getTotalSelectedClients = (...args) =>
  getSelectedClients(...args).length

export const sortSelectableTargetParams = (
  labelKeys,
  defaults,
  numberOfSections
) => {
  let sortedLabelsByClientsLength = sortBy(Object.keys(labelKeys), [
    // sort by defaults and then by number of unique values
    label => {
      // Using the indexOf we can ensure that if a label is a default it will
      // have a postive number and if not it will be `-1`
      return defaults.indexOf(label)
    },
    label => {
      // sort by the amount of unique values
      return labelKeys[label].values.size
    }
    // lodash sortBy is ascending so we reverse it so the "highest" are first
  ]).reverse()

  const splitLabels = sortedLabelsByClientsLength.reduce(
    ({ displayed, others }, labelKey) => {
      const currentLabelObj = labelKeys[labelKey]

      if (
        // Default label keys
        (defaults.includes(labelKey) ||
          // Label keys with 2 or more values
          currentLabelObj.values.size > 1) &&
        // Make sure we have one spot left over for "others"
        displayed.length < numberOfSections - 1
      ) {
        displayed.push(labelKey)
      } else {
        others.push(labelKey)
      }

      return {
        displayed,
        others
      }
    },
    {
      displayed: [],
      others: []
    }
  )

  return splitLabels
}

/**
 * This is used to determine the number of targets that will be impacted based on a percentage being supplied
 * @NOTE Only for use with percentage impacts
 *
 * @param {array} totalSelectedTargets
 * @param {integer} percentage
 */
export const getActualTargetsImpacted = (totalSelectedTargets, percentage) => {
  if (totalSelectedTargets === 0) {
    return 0
  }

  return Math.floor((percentage / 100) * totalSelectedTargets)
}

/**
 * Returns correct tag/label format based on the type of selection strategy being used
 * @NOTE Currently based on `multiselection-targeting-enabled` LD feature flag
 *
 * @param {String} targetingStrategy Checking since we're only impacting Random attacks
 */
export const getTargetParamsKey = (targetType, targetingStrategy) => {
  // Cleanup in attempt to catch any lowercase entries into this file
  const lowerTargetType = targetType.toLowerCase()

  if (targetingStrategy === 'Random') {
    if (lowerTargetType === 'hosts' || lowerTargetType === 'host') {
      return 'multiSelectTags'
    } else {
      return 'multiSelectLabels'
    }
  } else if (
    lowerTargetType === 'containers' ||
    lowerTargetType === 'container'
  ) {
    return 'labels'
  } else {
    return 'tags'
  }
}

export function convertParamsToSets(params = {}) {
  return Object.entries(params).reduce((params, [key, value]) => {
    if (Array.isArray(value)) {
      params[key] = new Set(value)
    } else {
      params[key] = new Set([value])
    }

    return params
  }, {})
}

export function getClientVersion(target) {
  return target.alfi_version || (target?.tags ?? {})['gremlin-client-version']
}

export function getClientVersionArray(version) {
  if (!version) return null
  return version.split('.').map(part => Number(part))
}

export function isOlderClientVersion(clientVersionArray, newerVersion) {
  if (clientVersionArray === null) return false
  if (!Array.isArray(clientVersionArray) || clientVersionArray.length < 1) {
    return true
  }

  for (let i = 0; i < clientVersionArray.length; i++) {
    if (clientVersionArray[i] < newerVersion[i]) {
      return true
    } else if (clientVersionArray[i] > newerVersion[i]) {
      return false
    }
  }
  return false
}

export function getLowestClientVersion(clients) {
  if (!clients || !Array.isArray(clients) || clients.length < 1) {
    return null
  }

  // start with a min at 100
  let minVersion = [100000, 0, 0]
  clients.forEach(target => {
    const clientVersionArray = getClientVersionArray(getClientVersion(target))
    // if the current version is newer than the max then assign the max to it
    // loop through until the end of the versions array
    if (
      isOlderClientVersion(clientVersionArray, minVersion) &&
      clientVersionArray
    ) {
      minVersion = clientVersionArray
    }
  })

  // if none of the clients had versions then return null
  if (minVersion.join('.') === '100000.0.0') {
    return null
  }

  return minVersion.join('.')
}

export function getHighestClientVersion(clients) {
  if (!clients || !Array.isArray(clients) || clients.length < 1) {
    return null
  }

  // start with a max at 0
  let maxVersionArray = [0, 0, 0]
  clients.forEach(target => {
    const clientVersionArray = getClientVersionArray(getClientVersion(target))
    // if the current version is newer than the max then assign the max to it
    // loop through until the end of the versions array
    if (
      !isOlderClientVersion(clientVersionArray, maxVersionArray) &&
      clientVersionArray
    ) {
      maxVersionArray = clientVersionArray
    }
  })

  // if none of the clients had versions then return null
  if (maxVersionArray.join('.') === '0.0.0') {
    return null
  }

  return maxVersionArray.join('.')
}

export function getTargetsWithDeprecatedVersion(clients = []) {
  return clients.reduce((accum, target) => {
    const clientVersionArray = getClientVersionArray(getClientVersion(target))
    if (
      isOlderClientVersion(
        clientVersionArray,
        target.os_type === 'Linux'
          ? NEW_CLIENT_VERSION_MIN
          : NEW_WINDOWS_CLIENT_VERSION_MIN
      )
    ) {
      accum.push(target)
    }
    return accum
  }, [])
}

export function getLabelDisplayName(name) {
  if (name) {
    switch (name) {
      case 'os-type':
        return 'Operating Systems'
      case 'service':
      case 'zone':
      case 'region':
        return capitalize(name)
      default:
        return name
    }
  }

  return ''
}

export function getClientsLastAttackedDateMap({ attacks = [], clients = [] }) {
  if (attacks.length === 0 || clients.length === 0) return {}

  let dateMap = {}
  attacks.forEach(attack => {
    if (attack.targets) {
      attack.targets.reduce((accum, targetId) => {
        if (!accum[targetId]) {
          accum[targetId] = attack.start_time
        } else if (moment(attack.start_time).isAfter(accum[targetId])) {
          accum[targetId] = attack.attack_startTime
        }
        return accum
      }, dateMap)
    }
  })

  return dateMap
}

export function isDockerClient(client) {
  if (client) {
    return 'docker_version' in client
  }

  return false
}

export function getK8sObjectContainerNames(k8Object = {}) {
  if (!k8Object.availableContainers) {
    return []
  }
  // get the list of names and filter out container without name prop
  return k8Object.availableContainers.map(obj => obj.name).filter(obj => obj)
}
